import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Avatar from '../Avatar';
import { formatPrice } from 'utils/numberUtils';
import { getNumericDate, getNumericDateTime } from 'utils/date';

/**
 * Componente para mostrar un KPI individual
 */
function KpiCard({ icon, label, value, variant = 'primary' }) {
	return (
		<Card className="text-center h-100 border-0 shadow-sm">
			<Card.Body className="py-3">
				<div className={`text-${variant} mb-2`}>
					<i className={`fa fa-${icon} fa-2x`}></i>
				</div>
				<h4 className="mb-1 fw-bold">{value}</h4>
				<small className="text-muted">{label}</small>
			</Card.Body>
		</Card>
	);
}

/**
 * Componente para mostrar la información básica del usuario con un diseño mejorado
 */
function InfoTab({ usuario = {}, estadisticas = null }) {
	// Preparar datos del usuario con valores por defecto
	// Corregido: ahora incluye ci, ruc, telefono y datosFacturacion
	const userInfo = {
		usuario: usuario?.usuario || '',
		nombre: usuario?.nombre || '',
		apellido: usuario?.apellido || '',
		email: usuario?.email || '',
		telefono: usuario?.telefono || '',
		tipo: usuario?.tipo || '',
		ci: usuario?.ci || '',
		ruc: usuario?.datosFacturacion?.[0]?.ruc || usuario?.ruc || '',
		razonSocial: usuario?.datosFacturacion?.[0]?.razonSocial || ''
	};

	const isCliente = userInfo.tipo?.toLowerCase() === 'cliente';

	// Obtener iniciales para el Avatar
	const getInitialsAndColor = () => {
		const name = `${userInfo.nombre} ${userInfo.apellido}`.trim();
		const initials = name
			.split(' ')
			.map(n => n && n[0])
			.filter(Boolean)
			.join('')
			.toUpperCase()
			.substring(0, 2) || '??';

		const colors = [
			'primary', 'success', 'danger', 'warning', 'info'
		];
		// Generar un color consistente basado en el ID de usuario
		const colorIndex = usuario?.id ? (usuario.id % colors.length) : 0;
		const bgColor = colors[colorIndex];

		return { initials, colorVariant: bgColor };
	};

	// Formatear valores para mostrar
	const formatValue = (value) => {
		if (value === null || value === undefined || value === '') {
			return 'No disponible';
		}
		return value;
	};

	const { initials, colorVariant } = getInitialsAndColor();

	return (
		<div className="profile-container py-4">
			<Row className="justify-content-center">
				<Col xs={12} md={10} lg={10}>
					{/* KPIs de estadísticas - Solo para clientes */}
					{isCliente && estadisticas && (
						<Row className="mb-4 g-3">
							<Col xs={6} md={3}>
								<KpiCard
									icon="shopping-bag"
									label="Total Pedidos"
									value={estadisticas.totalPedidos || 0}
									variant="primary"
								/>
							</Col>
							<Col xs={6} md={3}>
								<KpiCard
									icon="money-bill-wave"
									label="Total Comprado"
									value={formatPrice(estadisticas.montoTotal)}
									variant="success"
								/>
							</Col>
							<Col xs={6} md={3}>
								<KpiCard
									icon="receipt"
									label="Ticket Promedio"
									value={formatPrice(estadisticas.ticketPromedio)}
									variant="info"
								/>
							</Col>
							<Col xs={6} md={3}>
								<KpiCard
									icon="calendar-check"
									label="Último Pedido"
									value={estadisticas.ultimoPedido ?
										getNumericDate(estadisticas.ultimoPedido, 'Sin pedidos') :
										'Sin pedidos'}
									variant="warning"
								/>
							</Col>
						</Row>
					)}

					{/* Card principal del usuario */}
					<Card className="shadow-sm border-0 mb-4">
						<Card.Body className="p-0">
							<div className="bg-light d-flex align-items-center p-4 border-bottom">
								<div className="me-4">
									{usuario?.avatar && usuario.avatar !== false && usuario.avatar.id ? (
										<Avatar src={process.env.REACT_APP_API_URL + usuario.avatar.url} />
									) : (
										<Avatar initials={initials} variant={colorVariant} />
									)}
								</div>
								<div>
									<h3 className="mb-1 fw-bold">{`${userInfo.nombre} ${userInfo.apellido}`}</h3>
									<Badge bg={colorVariant} className="py-1 px-2">{userInfo.tipo}</Badge>
								</div>
							</div>

							<div className="p-4">
								<h5 className="text-muted mb-3 border-bottom pb-2">Información de Usuario</h5>
								<table className="table table-hover">
									<tbody>
										<tr>
											<th className="text-secondary" style={{ width: '30%' }}>ID</th>
											<td>{formatValue(usuario?.id)}</td>
										</tr>
										<tr>
											<th className="text-secondary">Usuario</th>
											<td>{formatValue(userInfo.usuario)}</td>
										</tr>
										<tr>
											<th className="text-secondary">Email</th>
											<td>{formatValue(userInfo.email)}</td>
										</tr>
										<tr>
											<th className="text-secondary">Teléfono</th>
											<td>{formatValue(userInfo.telefono)}</td>
										</tr>
										{isCliente ? (
											<>
												<tr>
													<th className="text-secondary">CI</th>
													<td>{formatValue(userInfo.ci)}</td>
												</tr>
												<tr>
													<th className="text-secondary">RUC</th>
													<td>{formatValue(userInfo.ruc)}</td>
												</tr>
												{userInfo.razonSocial && (
													<tr>
														<th className="text-secondary">Razón Social</th>
														<td>{formatValue(userInfo.razonSocial)}</td>
													</tr>
												)}
											</>
										) : (
											<tr>
												<th className="text-secondary">Tipo</th>
												<td>{formatValue(userInfo.tipo)}</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</Card.Body>
					</Card>

					{/* Productos más comprados - Solo para clientes con estadísticas */}
					{isCliente && estadisticas?.productosMasComprados?.length > 0 && (
						<Card className="shadow-sm border-0 mb-4">
							<Card.Body className="p-4">
								<h5 className="text-muted mb-3 border-bottom pb-2">
									<i className="fa fa-star me-2 text-warning"></i>
									Productos más comprados
								</h5>
								<table className="table table-hover">
									<thead>
										<tr>
											<th>Producto</th>
											<th className="text-center">Cantidad</th>
											<th className="text-end">Total</th>
										</tr>
									</thead>
									<tbody>
										{estadisticas.productosMasComprados.map((producto, index) => (
											<tr key={producto.itemId || index}>
												<td>
													<span className="fw-medium">{producto.nombre}</span>
													{producto.codigo && (
														<small className="text-muted d-block">
															Código: {producto.codigo}
														</small>
													)}
												</td>
												<td className="text-center">
													<Badge bg="secondary">{producto.cantidadTotal}</Badge>
												</td>
												<td className="text-end">
													{formatPrice(producto.montoTotal)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</Card.Body>
						</Card>
					)}

					{/* Tarjeta adicional para más información, si la hay */}
					{usuario?.fechaRegistro && (
						<Card className="shadow-sm border-0">
							<Card.Body className="p-4">
								<h5 className="text-muted mb-3 border-bottom pb-2">Detalles adicionales</h5>
								<table className="table table-hover">
									<tbody>
										<tr>
											<th className="text-secondary" style={{ width: '30%' }}>Fecha registro</th>
											<td>{getNumericDateTime(usuario.fechaRegistro)}</td>
										</tr>
										{usuario?.fechaUltimoAcceso && (
											<tr>
												<th className="text-secondary">Último acceso</th>
												<td>{getNumericDateTime(usuario.fechaUltimoAcceso)}</td>
											</tr>
										)}
									</tbody>
								</table>
							</Card.Body>
						</Card>
					)}

					{/* Ubicaciones del cliente */}
					{isCliente && usuario?.ubicaciones?.length > 0 && (
						<Card className="shadow-sm border-0 mt-4">
							<Card.Body className="p-4">
								<h5 className="text-muted mb-3 border-bottom pb-2">
									<i className="fa fa-map-marker-alt me-2 text-danger"></i>
									Direcciones guardadas
								</h5>
								{usuario.ubicaciones.map((ubicacion, index) => (
									<div key={ubicacion.id || index} className="border-bottom py-2">
										<div className="fw-medium">
											{ubicacion.direccion || 'Dirección no especificada'}
										</div>
										{ubicacion.referencia && (
											<small className="text-muted">{ubicacion.referencia}</small>
										)}
									</div>
								))}
							</Card.Body>
						</Card>
					)}
				</Col>
			</Row>
		</div>
	);
}

export default InfoTab;